import { z } from 'zod';
import {
  SENDING_MODES,
  type CustomerEmailWait,
  type SendingMode,
} from './customer-emails';
import type { ReplyAddressStatus } from './platform-email';
import { addDaysYmd, localClockOf, localDateOf } from './mcp/timezone';

/**
 * Settings → Email and the app-wide email banner (MAP 249 spec §9, ticket 07): ONE server read,
 * `email.status`, answers both. The server decides what is true about the shop's email (the
 * route, today's count, what waits, whether the shop is paused or held, which notices apply);
 * the screens only say it. A read that failed or never came back is UNKNOWN on every screen:
 * nothing here has a default that means "all good".
 */

/** "Your domain" as Settings shows it (ticket 08 writes the row; this reads it). */
export interface EmailDomainState {
  domain: string;
  /** Resend verified it and ServWell's `_servwell` token was seen (spec §7). */
  verified: boolean;
  /** It stopped working: email goes from ServWell's address meanwhile (spec §7). */
  failingSince: Date | null;
  /**
   * Its name looks like a brand on ServWell's list: it never sends until staff clear that exact
   * domain (spec §7, ticket 08).
   */
  held: boolean;
}

/** "Your mailbox" as Settings shows it (ticket 10 writes the row; this reads it). */
export interface EmailMailboxState {
  provider: 'google' | 'microsoft';
  address: string;
  status: 'connected' | 'needs_reconnect';
}

/**
 * Something about the shop's email that a person should know, MOST URGENT FIRST in
 * `EmailStatus.notices` (the order of `EMAIL_NOTICE_KINDS`). Each is derived by the server from
 * rows that exist; there is no notice a screen invents.
 */
export type EmailNotice =
  /** Staff paused the shop's email (`shop_email_settings.paused_at`, spec §10). */
  | { kind: 'paused' }
  /** The gate holds the shop by its name (spec §4): the same rule every send reads. */
  | { kind: 'held' }
  /**
   * The shop's confirmed reply address, or its domain's From name (the part before the @), looks
   * like a brand by the CURRENT rules (MAP 249 Codex r2 #5): customer emails wait until the owner
   * changes it. `address` is the one to change.
   */
  | { kind: 'sender_held'; what: 'reply_address' | 'from_name'; address: string }
  /** No owner of the shop has confirmed their sign-up email (spec §11b). */
  | { kind: 'owner_unconfirmed' }
  /** The shop sends from its own domain and it stopped working (spec §7). */
  | { kind: 'domain_failing'; domain: string }
  /** The shop sends from its mailbox and the connection broke (spec §8). */
  | { kind: 'mailbox_reconnect'; address: string }
  /** Emails waiting for ServWell to check them (`content_review`, spec §4b). */
  | { kind: 'content_review'; count: number }
  /** Ticket emails waiting for a confirmed reply address (spec §5). */
  | { kind: 'reply_address'; count: number }
  /**
   * Emails waiting on a daily limit (spec §2 step 6): FACTS ONLY (Codex 07-r3). How many, today's
   * counter on the route as it stands (`today`), and the earliest waiting row's OWN scheduled
   * time (`scheduled`: its `not_before` / `next_attempt_at`, kept as the row has it). Nothing
   * here projects what the drain will do with it (it may find a limit again, or staff may release
   * it at once); a past schedule is "due for retry", never "being retried" (no row is sending).
   */
  | {
      kind: 'daily_limit';
      count: number;
      today: { sent: number; cap: number };
      scheduled: WaitTry;
    }
  /** Emails waiting on a limit outside the shop: ServWell's ceiling or a mailbox's (§8, §11b). */
  | { kind: 'route_limit'; count: number };

export type EmailNoticeKind = EmailNotice['kind'];

/**
 * A waiting row's scheduled time, as the shop's wall clock reads it, kept exactly as the row has
 * it (never rewritten to now, never moved to a time the drain has not recorded). One already past
 * is `overdue`: due for retry.
 */
export interface WaitTry {
  /** Relative to the shop's today. */
  day: 'earlier' | 'today' | 'tomorrow' | 'later';
  /** The shop-local date (`YYYY-MM-DD`). */
  date: string;
  /** The shop-local time, "8:00 AM". */
  time: string;
  /** Scheduled before now: due for retry. */
  overdue: boolean;
}

/** "8:00 AM", "12:05 PM": a wall-clock reading as a person says it. */
export function wallTimeLabel(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** `at` (a scheduled try) on the shop's clock, relative to `now`. */
export function waitTry(at: Date, now: Date, timeZone: string): WaitTry {
  const { date, hour, minute } = localClockOf(at, timeZone);
  const today = localDateOf(now, timeZone);
  const day =
    date < today
      ? 'earlier'
      : date === today
        ? 'today'
        : date === addDaysYmd(today, 1)
          ? 'tomorrow'
          : 'later';
  return {
    day,
    date,
    time: wallTimeLabel(hour, minute),
    overdue: at.getTime() < now.getTime(),
  };
}

/** "8:00 AM", "8:00 AM tomorrow", "8:00 AM on 2026-10-17": a schedule still to come. */
export function scheduledFor(t: WaitTry): string {
  if (t.day === 'today') return t.time;
  if (t.day === 'tomorrow') return `${t.time} tomorrow`;
  return `${t.time} on ${t.date}`;
}

/**
 * The earliest waiting row's schedule, as a fact: "it is scheduled for 8:00 AM", "the first is
 * scheduled for 8:00 AM tomorrow"; a past one "is due for retry".
 */
function scheduleWords(n: Extract<EmailNotice, { kind: 'daily_limit' }>): string {
  const who = n.count === 1 ? 'it' : 'the first';
  return n.scheduled.overdue
    ? `${who} is due for retry`
    : `${who} is scheduled for ${scheduledFor(n.scheduled)}`;
}

/**
 * One line for the emails waiting on a daily limit, without a count (Settings → Email's "what
 * waits" list); today's counter is the line above it.
 */
export function dailyLimitWaitLine(
  n: Extract<EmailNotice, { kind: 'daily_limit' }>,
): string {
  return `Waiting for a daily email limit; ${scheduleWords(n)}`;
}

/** Every notice, in the order the server lists them (most urgent first). */
export const EMAIL_NOTICE_KINDS = [
  'paused',
  'held',
  'sender_held',
  'owner_unconfirmed',
  'domain_failing',
  'mailbox_reconnect',
  'content_review',
  'reply_address',
  'daily_limit',
  'route_limit',
] as const satisfies readonly EmailNoticeKind[];

/** Settings → Email's one read. */
export interface EmailStatus {
  /**
   * Can a customer email leave this deployment at all (the shop-mail sender is configured)?
   * False: every customer email is written `skipped / not-configured`, and `notices` is empty.
   */
  on: boolean;
  /** The route the shop chose (`shop_email_settings.sending_mode`). */
  sendingMode: SendingMode;
  /** The route its customer emails go by now: the chosen one while it works, else ServWell's. */
  route: SendingMode;
  /** May the owner choose each route right now (it is set up and working)? */
  ready: Record<SendingMode, boolean>;
  domain: EmailDomainState | null;
  mailbox: EmailMailboxState | null;
  /**
   * Today (the shop's day) on the route's counter: emails that went or are going, the cap, and
   * whether the shop is in its first day (every route's cap is lower then).
   */
  today: { date: string; sent: number; cap: number; firstDay: boolean };
  /** Customer and ticket emails waiting, by why (only the reasons with any). */
  waiting: Partial<Record<CustomerEmailWait, number>>;
  /** The gate's own answers about the shop (spec §2 steps 1, 2, 2a). */
  paused: boolean;
  held: boolean;
  ownerConfirmed: boolean;
  /** The person asking: are they an owner here, and is their own sign-up address confirmed? */
  you: { owner: boolean; email: string; confirmed: boolean };
  replyAddress: ReplyAddressStatus;
  /** The review link customers are sent to (ticket 06 writes it). */
  reviewLink: string | null;
  /** What needs saying, most urgent first: the banner and the card read this one list. */
  notices: EmailNotice[];
}

/** How many emails a count-carrying notice is about, or null for a shop-wide one. */
export function noticeCount(n: EmailNotice): number | null {
  return 'count' in n ? n.count : null;
}

const emails = (count: number) => (count === 1 ? '1 email' : `${count} emails`);
const ticketEmails = (count: number) =>
  count === 1 ? '1 message for a customer' : `${count} messages for customers`;

/**
 * The sentence for each notice (spec §9's words where it gives them), addressed to the shop.
 * One place, so the banner and the card cannot say two different things about one fact.
 */
export function emailNoticeLine(n: EmailNotice): string {
  switch (n.kind) {
    case 'paused':
      return 'ServWell has paused your customer emails. Nothing goes until it resumes them. Contact ServWell.';
    case 'held':
      return 'Your customer emails are paused while ServWell reviews your shop name. We’ll be in touch.';
    case 'sender_held':
      return n.what === 'reply_address'
        ? `Customer emails wait: your reply address ${n.address} looks like another company’s. Change it to your shop’s own address.`
        : `Customer emails wait: your domain’s From address ${n.address} looks like another company’s. Change the name before the @.`;
    case 'owner_unconfirmed':
      return 'Customer emails wait until the shop’s owner confirms their email address.';
    case 'domain_failing':
      return `Your domain ${n.domain} stopped working: we’re sending from ServWell’s address until it’s fixed.`;
    case 'mailbox_reconnect':
      return `Your mailbox ${n.address} needs to be connected again: we’re sending from ServWell’s address until then.`;
    case 'content_review':
      return `${emails(n.count)} ${n.count === 1 ? 'is' : 'are'} waiting for ServWell to check ${n.count === 1 ? 'it' : 'them'} before ${n.count === 1 ? 'it goes' : 'they go'}.`;
    case 'reply_address':
      return `${ticketEmails(n.count)} ${n.count === 1 ? 'is' : 'are'} waiting for your reply address to be confirmed.`;
    case 'daily_limit': {
      const tail = scheduleWords(n);
      return `${emails(n.count)} ${n.count === 1 ? 'is' : 'are'} waiting for a daily email limit (today: ${n.today.sent} of ${n.today.cap} sent). ${tail.charAt(0).toUpperCase()}${tail.slice(1)}.`;
    }
    case 'route_limit':
      return `${emails(n.count)} ${n.count === 1 ? 'is' : 'are'} waiting: a sending limit outside your shop is used up for now.`;
  }
}

/**
 * The owner's own line for `owner_unconfirmed`: they are the one who can fix it. The button
 * beside it is "Send it again" (`me.resendEmailConfirmation`).
 */
export function ownerUnconfirmedLine(address: string): string {
  return `Confirm your email address to start sending to customers. We emailed a link to ${address}.`;
}

// ── The owner's write ────────────────────────────────────────────────────────────────────

/**
 * What Settings → Email may change (`email.update`, owner only). `.strict()`: a key it does not
 * name is REFUSED, never quietly dropped, so a staff column (`paused_*`, `held_*`,
 * `hold_cleared_*`, `daily_limit_override`, `mailbox_pilot`) cannot be sent through it at all.
 * The service picks the same columns again (`OWNER_EMAIL_SETTINGS`), because 0045 grants the app
 * role UPDATE on the staff columns too (the staff command's role inherits from it).
 */
export const emailSettingsInput = z
  .object({
    sendingMode: z.enum(SENDING_MODES).optional(),
    /**
     * The shop's review link (ticket 06), or null / empty to take it off. Its allow-list and the
     * content rules are the SERVICE's (`reviewLinkRefusal`), refused in words; here only its type
     * and a bound.
     */
    reviewLink: z.string().max(2000).nullable().optional(),
  })
  .strict();
export type EmailSettingsInput = z.infer<typeof emailSettingsInput>;

/** The owner chose a route that is not set up, or not working, yet. */
export const SENDING_MODE_NOT_READY: Record<Exclude<SendingMode, 'servwell'>, string> = {
  domain:
    'Your domain isn’t ready to send from yet. Finish setting it up, or keep sending from ServWell’s address.',
  mailbox:
    'Your mailbox isn’t connected yet. Connect it first, or keep sending from ServWell’s address.',
};
